import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  liveStatusTranslationKey,
  operationsControlRoomTopic
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogFinancialPostingRecord,
  CatalogOperationsPostingProfile,
  CatalogService,
  HospitalityKpi,
  HospitalityProperty,
  OperationsControlRoomSummary,
  ServiceSlaEscalation,
  UpsertCatalogOperationsPostingProfilePayload
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-operations-control-room',
  imports: [
    DecimalPipe, AinPageComponent, AinStateComponent, RouterLink, ButtonModule, FormsModule, TableModule,
    CheckboxModule, CurrencySelectorComponent, FinancialAccountSelectorComponent
  ],
  template: `
    <ain-page [title]="t('catalog.controlRoom.title')" [description]="t('catalog.controlRoom.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!summary()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" />
      } @else {
        <div class="toolbar">
          <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-status" [class]="liveUpdateService.connectionState()">
            {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
          </span>
        </div>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.tracking') }}</h3>
          <div class="stats">
            <article><strong>{{ t('catalog.controlRoom.totalLots') }}</strong><span>{{ summary()!.tracking.totalLots }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.totalSerials') }}</strong><span>{{ summary()!.tracking.totalSerials }}</span></article>
            <article class="warn"><strong>{{ t('catalog.controlRoom.quarantined') }}</strong><span>{{ summary()!.tracking.quarantinedLots + summary()!.tracking.quarantinedSerials }}</span></article>
            <article class="warn"><strong>{{ t('catalog.controlRoom.expiring30') }}</strong><span>{{ summary()!.tracking.expiringWithin30Days }}</span></article>
          </div>
          <a [routerLink]="['/inventory/tracking-dashboard']">{{ t('catalog.controlRoom.openTracking') }}</a>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.quality') }}</h3>
          <div class="stats">
            <article><strong>{{ t('catalog.controlRoom.qcPending') }}</strong><span>{{ summary()!.quality.pendingCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.qcInProgress') }}</strong><span>{{ summary()!.quality.inProgressCount }}</span></article>
            <article class="warn"><strong>{{ t('catalog.controlRoom.qcFailed') }}</strong><span>{{ summary()!.quality.failedCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.qcPassed') }}</strong><span>{{ summary()!.quality.passedCount }}</span></article>
          </div>
          <a [routerLink]="['/inventory/qc-inspections']">{{ t('catalog.controlRoom.openQc') }}</a>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.recall') }}</h3>
          <div class="stats">
            <article><strong>Draft</strong><span>{{ summary()!.recall.draftCount }}</span></article>
            <article><strong>PendingApproval</strong><span>{{ summary()!.recall.pendingApprovalCount }}</span></article>
            <article class="warn"><strong>Executing</strong><span>{{ summary()!.recall.executingCount }}</span></article>
            <article><strong>Completed</strong><span>{{ summary()!.recall.completedCount }}</span></article>
          </div>
          <a [routerLink]="['/inventory/recalls']">{{ t('catalog.controlRoom.openRecall') }}</a>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.service') }}</h3>
          <div class="stats">
            <article><strong>{{ t('catalog.controlRoom.scheduled') }}</strong><span>{{ summary()!.service.scheduledCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.inProgress') }}</strong><span>{{ summary()!.service.inProgressCount }}</span></article>
            <article class="warn"><strong>{{ t('catalog.controlRoom.slaBreached') }}</strong><span>{{ summary()!.service.slaBreachedCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.pendingApproval') }}</strong><span>{{ summary()!.service.pendingCustomerApprovalCount }}</span></article>
          </div>
          <div class="links">
            <a [routerLink]="['/services/dispatch-board']">{{ t('catalog.controlRoom.openDispatch') }}</a>
            <a [routerLink]="['/services/technician']">{{ t('catalog.controlRoom.openTechnician') }}</a>
          </div>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.slaEscalations') }}</h3>
          <p-table [value]="slaEscalations()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.controlRoom.slaOrder') }}</th>
                <th>{{ t('catalog.controlRoom.slaReason') }}</th>
                <th>{{ t('catalog.controlRoom.slaStatus') }}</th>
                <th>{{ t('catalog.controlRoom.slaEscalatedAt') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [class.warn-row]="row.status === 'Open'">
                <td><a [routerLink]="['/services/orders', row.serviceOrderId]">{{ row.serviceOrderId }}</a></td>
                <td>{{ row.reason }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.escalatedAtUtc }}</td>
                <td>
                  @if (row.status === 'Open') {
                    <button pButton type="button" size="small" [label]="t('catalog.controlRoom.acknowledgeSla')" (click)="acknowledgeSla(row.id)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5">{{ t('catalog.controlRoom.noSlaEscalations') }}</td></tr>
            </ng-template>
          </p-table>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.assets') }}</h3>
          <div class="stats">
            <article><strong>{{ t('catalog.controlRoom.assetsInService') }}</strong><span>{{ summary()!.assets.inServiceCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.assetsMaintenance') }}</strong><span>{{ summary()!.assets.underMaintenanceCount }}</span></article>
            <article class="warn"><strong>{{ t('catalog.controlRoom.pendingDisposal') }}</strong><span>{{ summary()!.assets.pendingDisposalApprovalCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.draftDepRuns') }}</strong><span>{{ summary()!.assets.draftDepreciationRunCount }}</span></article>
          </div>
          <a [routerLink]="['/assets/register']">{{ t('catalog.controlRoom.openAssets') }}</a>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.rentalOps') }}</h3>
          <div class="stats">
            <article><strong>{{ t('catalog.controlRoom.rentalAvailable') }}</strong><span>{{ summary()!.rental.availableUnitCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.rentalRented') }}</strong><span>{{ summary()!.rental.rentedUnitCount }}</span></article>
            <article class="warn"><strong>{{ t('catalog.controlRoom.rentalMaintenance') }}</strong><span>{{ summary()!.rental.maintenanceUnitCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.activeAgreements') }}</strong><span>{{ summary()!.rental.activeAgreementCount }}</span></article>
          </div>
          <div class="links">
            <a [routerLink]="['/rental/booking-board']">{{ t('catalog.rental.bookingBoard.title') }}</a>
            <a [routerLink]="['/rental/agreements']">{{ t('catalog.rental.agreements.title') }}</a>
          </div>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.hospitalityOps') }}</h3>
          @if (hospitalityKpi()) {
            <div class="stats">
              <article><strong>{{ t('catalog.hospitality.kpi.occupancyRate') }}</strong><span>{{ hospitalityKpi()!.occupancyRate | number:'1.1-1' }}%</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.adr') }}</strong><span>{{ hospitalityKpi()!.averageDailyRate | number:'1.2-2' }}</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.revPar') }}</strong><span>{{ hospitalityKpi()!.revenuePerAvailableRoom | number:'1.2-2' }}</span></article>
              <article class="warn"><strong>{{ t('catalog.hospitality.kpi.pendingHousekeeping') }}</strong><span>{{ hospitalityKpi()!.pendingHousekeepingTasks }}</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.activeStays') }}</strong><span>{{ hospitalityKpi()!.activeStays }}</span></article>
              <article><strong>{{ t('catalog.hospitality.kpi.confirmedReservations') }}</strong><span>{{ hospitalityKpi()!.confirmedReservations }}</span></article>
            </div>
          } @else {
            <p>{{ t('catalog.controlRoom.hospitalityEmpty') }}</p>
          }
          <div class="links">
            <a [routerLink]="['/hospitality/kpi-dashboard']">{{ t('catalog.hospitality.kpi.title') }}</a>
            <a [routerLink]="['/hospitality/front-desk']">{{ t('catalog.hospitality.frontDesk.title') }}</a>
            <a [routerLink]="['/hospitality/reservations']">{{ t('catalog.hospitality.reservations.title') }}</a>
          </div>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.cmmsOps') }}</h3>
          <div class="stats">
            <article><strong>{{ t('catalog.cmms.plans.title') }}</strong><span>{{ summary()!.cmms.activePlanCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.openWorkOrders') }}</strong><span>{{ summary()!.cmms.openWorkOrderCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.inProgressWorkOrders') }}</strong><span>{{ summary()!.cmms.inProgressWorkOrderCount }}</span></article>
            <article class="warn"><strong>{{ t('catalog.cmms.downtime.openCount') }}</strong><span>{{ summary()!.cmms.openDowntimeCount }}</span></article>
          </div>
          <div class="links">
            <a [routerLink]="['/cmms/work-orders']">{{ t('catalog.cmms.workOrders.title') }}</a>
            <a [routerLink]="['/cmms/plans']">{{ t('catalog.cmms.plans.title') }}</a>
          </div>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.rma') }}</h3>
          <div class="stats">
            <article><strong>{{ t('catalog.controlRoom.rmaSubmitted') }}</strong><span>{{ summary()!.rma.submittedCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.rmaDiagnosed') }}</strong><span>{{ summary()!.rma.diagnosedCount }}</span></article>
            <article class="warn"><strong>{{ t('catalog.controlRoom.rmaPendingApproval') }}</strong><span>{{ summary()!.rma.pendingApprovalCount }}</span></article>
            <article><strong>{{ t('catalog.controlRoom.rmaResolved') }}</strong><span>{{ summary()!.rma.resolvedCount }}</span></article>
          </div>
          <a [routerLink]="['/services/rma']">{{ t('catalog.controlRoom.openRma') }}</a>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.postingProfile') }}</h3>
          <div class="form-grid">
            <label class="checkbox-row full">
              <p-checkbox [(ngModel)]="postingForm.isEnabled" [binary]="true" />
              <span>{{ t('catalog.controlRoom.postingEnabled') }}</span>
            </label>
            <label>
              <span>{{ t('catalog.controlRoom.defaultCurrency') }}</span>
              <ain-currency-selector [(ngModel)]="postingForm.defaultCurrencyCode" />
            </label>
            @for (field of postingAccountFields; track field.key) {
              <label>
                <span>{{ t(field.labelKey) }}</span>
                <ain-financial-account-selector
                  [ngModel]="getPostingAccountValue(field.key)"
                  (ngModelChange)="setPostingAccountValue(field.key, $event)" />
              </label>
            }
            <div class="full actions-row">
              <button pButton type="button" [label]="t('catalog.controlRoom.bootstrapPhase3')" [disabled]="bootstrappingPosting()" (click)="bootstrapPhase3Posting()"></button>
              <button pButton type="button" [label]="t('catalog.controlRoom.bootstrapPhase4')" [disabled]="bootstrappingPosting()" (click)="bootstrapPhase4Posting()"></button>
              <button pButton type="button" [label]="t('catalog.controlRoom.bootstrapPhase5')" [disabled]="bootstrappingPosting()" (click)="bootstrapPhase5Posting()"></button>
              <button pButton type="button" [label]="t('catalog.controlRoom.savePostingProfile')" [disabled]="savingPosting()" (click)="savePostingProfile()"></button>
              @if (postingMessage()) {
                <span class="message">{{ postingMessage() }}</span>
              }
            </div>
          </div>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.inventoryGlOps') }}</h3>
          <p>{{ t('catalog.controlRoom.inventoryGlOpsHint') }}</p>
          <a [routerLink]="['/inventory/costing/operability']">{{ t('catalog.controlRoom.openInventoryOperability') }}</a>
        </section>

        <section class="section">
          <h3>{{ t('catalog.controlRoom.financialPostings') }}</h3>
          <p-table [value]="financialPostings()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.controlRoom.postingEvent') }}</th>
                <th>{{ t('catalog.controlRoom.postingAmount') }}</th>
                <th>{{ t('catalog.controlRoom.postingStatus') }}</th>
                <th>{{ t('catalog.controlRoom.postingCreatedAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.eventType }}</td>
                <td>{{ row.amount }} {{ row.currencyCode }}</td>
                <td [class.warn-cell]="row.status === 'Failed'">{{ row.status }}</td>
                <td>{{ row.createdAtUtc }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="4">{{ t('catalog.controlRoom.noFinancialPostings') }}</td></tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; gap: 8px; align-items: center; }
    .live-status {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #6b7280;
    }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
    .section { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin-bottom: 12px; }
    .section h3 { margin: 0 0 10px; font-size: 16px; }
    .stats { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); margin-bottom: 10px; }
    .stats article { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; display: grid; gap: 4px; }
    .stats article.warn { border-color: #fca5a5; background: #fef2f2; }
    .stats strong { font-size: 11px; color: #64748b; }
    .links { display: flex; gap: 12px; flex-wrap: wrap; }
    a { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .warn-row { background: #fef2f2; }
    .warn-cell { color: #b91c1c; font-weight: 600; }
    .form-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .form-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .actions-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
    .message { font-size: 12px; color: #15803d; }
  `]
})
export class OperationsControlRoomComponent implements OnInit, OnDestroy {
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';

  readonly loading = signal(true);
  readonly summary = signal<OperationsControlRoomSummary | null>(null);
  readonly hospitalityKpi = signal<HospitalityKpi | null>(null);
  readonly slaEscalations = signal<ServiceSlaEscalation[]>([]);
  readonly financialPostings = signal<CatalogFinancialPostingRecord[]>([]);
  readonly savingPosting = signal(false);
  readonly bootstrappingPosting = signal(false);
  readonly postingMessage = signal('');

  readonly postingAccountFields: { key: PostingAccountFieldKey; labelKey: string }[] = [
    { key: 'serviceRevenueDebitAccountId', labelKey: 'catalog.controlRoom.serviceRevenueDebit' },
    { key: 'serviceRevenueCreditAccountId', labelKey: 'catalog.controlRoom.serviceRevenueCredit' },
    { key: 'servicePartsCostDebitAccountId', labelKey: 'catalog.controlRoom.servicePartsDebit' },
    { key: 'servicePartsCostCreditAccountId', labelKey: 'catalog.controlRoom.servicePartsCredit' },
    { key: 'rmaRefundDebitAccountId', labelKey: 'catalog.controlRoom.rmaRefundDebit' },
    { key: 'rmaRefundCreditAccountId', labelKey: 'catalog.controlRoom.rmaRefundCredit' },
    { key: 'warrantyExpenseDebitAccountId', labelKey: 'catalog.controlRoom.warrantyExpenseDebit' },
    { key: 'warrantyExpenseCreditAccountId', labelKey: 'catalog.controlRoom.warrantyExpenseCredit' },
    { key: 'assetCapitalizationDebitAccountId', labelKey: 'catalog.controlRoom.assetCapitalizationDebit' },
    { key: 'assetCapitalizationCreditAccountId', labelKey: 'catalog.controlRoom.assetCapitalizationCredit' },
    { key: 'assetDepreciationExpenseDebitAccountId', labelKey: 'catalog.controlRoom.assetDepreciationDebit' },
    { key: 'assetDepreciationCreditAccountId', labelKey: 'catalog.controlRoom.assetDepreciationCredit' },
    { key: 'assetDisposalGainLossDebitAccountId', labelKey: 'catalog.controlRoom.assetDisposalDebit' },
    { key: 'assetDisposalGainLossCreditAccountId', labelKey: 'catalog.controlRoom.assetDisposalCredit' },
    { key: 'rentalRevenueDebitAccountId', labelKey: 'catalog.controlRoom.rentalRevenueDebit' },
    { key: 'rentalRevenueCreditAccountId', labelKey: 'catalog.controlRoom.rentalRevenueCredit' },
    { key: 'rentalDepositDebitAccountId', labelKey: 'catalog.controlRoom.rentalDepositDebit' },
    { key: 'rentalDepositCreditAccountId', labelKey: 'catalog.controlRoom.rentalDepositCredit' },
    { key: 'rentalDamageChargeDebitAccountId', labelKey: 'catalog.controlRoom.rentalDamageDebit' },
    { key: 'rentalDamageChargeCreditAccountId', labelKey: 'catalog.controlRoom.rentalDamageCredit' },
    { key: 'cmmsMaintenanceExpenseDebitAccountId', labelKey: 'catalog.controlRoom.cmmsMaintenanceDebit' },
    { key: 'cmmsMaintenanceExpenseCreditAccountId', labelKey: 'catalog.controlRoom.cmmsMaintenanceCredit' },
    { key: 'cmmsSparePartConsumptionDebitAccountId', labelKey: 'catalog.controlRoom.cmmsSparePartsDebit' },
    { key: 'cmmsSparePartConsumptionCreditAccountId', labelKey: 'catalog.controlRoom.cmmsSparePartsCredit' },
    { key: 'hospitalityPrepaymentDebitAccountId', labelKey: 'catalog.controlRoom.hospitalityPrepaymentDebit' },
    { key: 'hospitalityPrepaymentCreditAccountId', labelKey: 'catalog.controlRoom.hospitalityPrepaymentCredit' },
    { key: 'hospitalityFolioChargeDebitAccountId', labelKey: 'catalog.controlRoom.hospitalityFolioDebit' },
    { key: 'hospitalityFolioChargeCreditAccountId', labelKey: 'catalog.controlRoom.hospitalityFolioCredit' },
    { key: 'hospitalityRefundDebitAccountId', labelKey: 'catalog.controlRoom.hospitalityRefundDebit' },
    { key: 'hospitalityRefundCreditAccountId', labelKey: 'catalog.controlRoom.hospitalityRefundCredit' },
    { key: 'hospitalityMinibarCogsDebitAccountId', labelKey: 'catalog.controlRoom.hospitalityMinibarCogsDebit' },
    { key: 'hospitalityMinibarCogsCreditAccountId', labelKey: 'catalog.controlRoom.hospitalityMinibarCogsCredit' },
    { key: 'hospitalityChannelCommissionDebitAccountId', labelKey: 'catalog.controlRoom.hospitalityCommissionDebit' },
    { key: 'hospitalityChannelCommissionCreditAccountId', labelKey: 'catalog.controlRoom.hospitalityCommissionCredit' },
    { key: 'inventoryIssueDebitAccountId', labelKey: 'catalog.controlRoom.inventoryIssueDebit' },
    { key: 'inventoryIssueCreditAccountId', labelKey: 'catalog.controlRoom.inventoryIssueCredit' },
    { key: 'inventoryCogsDebitAccountId', labelKey: 'catalog.controlRoom.inventoryCogsDebit' },
    { key: 'inventoryCogsCreditAccountId', labelKey: 'catalog.controlRoom.inventoryCogsCredit' },
    { key: 'inventoryReceiptDebitAccountId', labelKey: 'catalog.controlRoom.inventoryReceiptDebit' },
    { key: 'inventoryReceiptCreditAccountId', labelKey: 'catalog.controlRoom.inventoryReceiptCredit' },
    { key: 'inventoryAdjustmentGainDebitAccountId', labelKey: 'catalog.controlRoom.inventoryAdjustmentGainDebit' },
    { key: 'inventoryAdjustmentGainCreditAccountId', labelKey: 'catalog.controlRoom.inventoryAdjustmentGainCredit' },
    { key: 'inventoryAdjustmentLossDebitAccountId', labelKey: 'catalog.controlRoom.inventoryAdjustmentLossDebit' },
    { key: 'inventoryAdjustmentLossCreditAccountId', labelKey: 'catalog.controlRoom.inventoryAdjustmentLossCredit' },
    { key: 'inventoryLandedCostDebitAccountId', labelKey: 'catalog.controlRoom.inventoryLandedCostDebit' },
    { key: 'inventoryLandedCostCreditAccountId', labelKey: 'catalog.controlRoom.inventoryLandedCostCredit' },
    { key: 'inventoryRevaluationGainDebitAccountId', labelKey: 'catalog.controlRoom.inventoryRevaluationGainDebit' },
    { key: 'inventoryRevaluationGainCreditAccountId', labelKey: 'catalog.controlRoom.inventoryRevaluationGainCredit' },
    { key: 'inventoryRevaluationLossDebitAccountId', labelKey: 'catalog.controlRoom.inventoryRevaluationLossDebit' },
    { key: 'inventoryRevaluationLossCreditAccountId', labelKey: 'catalog.controlRoom.inventoryRevaluationLossCredit' },
    { key: 'inventoryStandardVarianceGainDebitAccountId', labelKey: 'catalog.controlRoom.inventoryStandardVarianceGainDebit' },
    { key: 'inventoryStandardVarianceGainCreditAccountId', labelKey: 'catalog.controlRoom.inventoryStandardVarianceGainCredit' },
    { key: 'inventoryStandardVarianceLossDebitAccountId', labelKey: 'catalog.controlRoom.inventoryStandardVarianceLossDebit' },
    { key: 'inventoryStandardVarianceLossCreditAccountId', labelKey: 'catalog.controlRoom.inventoryStandardVarianceLossCredit' },
    { key: 'inventoryTransferDebitAccountId', labelKey: 'catalog.controlRoom.inventoryTransferDebit' },
    { key: 'inventoryTransferCreditAccountId', labelKey: 'catalog.controlRoom.inventoryTransferCredit' }
  ];

  readonly postingForm = {
    isEnabled: false,
    defaultCurrencyCode: 'USD',
    serviceRevenueDebitAccountId: '',
    serviceRevenueCreditAccountId: '',
    servicePartsCostDebitAccountId: '',
    servicePartsCostCreditAccountId: '',
    rmaRefundDebitAccountId: '',
    rmaRefundCreditAccountId: '',
    warrantyExpenseDebitAccountId: '',
    warrantyExpenseCreditAccountId: '',
    assetCapitalizationDebitAccountId: '',
    assetCapitalizationCreditAccountId: '',
    assetDepreciationExpenseDebitAccountId: '',
    assetDepreciationCreditAccountId: '',
    assetDisposalGainLossDebitAccountId: '',
    assetDisposalGainLossCreditAccountId: '',
    rentalRevenueDebitAccountId: '',
    rentalRevenueCreditAccountId: '',
    rentalDepositDebitAccountId: '',
    rentalDepositCreditAccountId: '',
    rentalDamageChargeDebitAccountId: '',
    rentalDamageChargeCreditAccountId: '',
    cmmsMaintenanceExpenseDebitAccountId: '',
    cmmsMaintenanceExpenseCreditAccountId: '',
    cmmsSparePartConsumptionDebitAccountId: '',
    cmmsSparePartConsumptionCreditAccountId: '',
    hospitalityPrepaymentDebitAccountId: '',
    hospitalityPrepaymentCreditAccountId: '',
    hospitalityFolioChargeDebitAccountId: '',
    hospitalityFolioChargeCreditAccountId: '',
    hospitalityRefundDebitAccountId: '',
    hospitalityRefundCreditAccountId: '',
    hospitalityMinibarCogsDebitAccountId: '',
    hospitalityMinibarCogsCreditAccountId: '',
    hospitalityChannelCommissionDebitAccountId: '',
    hospitalityChannelCommissionCreditAccountId: '',
    inventoryIssueDebitAccountId: '',
    inventoryIssueCreditAccountId: '',
    inventoryCogsDebitAccountId: '',
    inventoryCogsCreditAccountId: '',
    inventoryReceiptDebitAccountId: '',
    inventoryReceiptCreditAccountId: '',
    inventoryAdjustmentGainDebitAccountId: '',
    inventoryAdjustmentGainCreditAccountId: '',
    inventoryAdjustmentLossDebitAccountId: '',
    inventoryAdjustmentLossCreditAccountId: '',
    inventoryLandedCostDebitAccountId: '',
    inventoryLandedCostCreditAccountId: '',
    inventoryRevaluationGainDebitAccountId: '',
    inventoryRevaluationGainCreditAccountId: '',
    inventoryRevaluationLossDebitAccountId: '',
    inventoryRevaluationLossCreditAccountId: '',
    inventoryStandardVarianceGainDebitAccountId: '',
    inventoryStandardVarianceGainCreditAccountId: '',
    inventoryStandardVarianceLossDebitAccountId: '',
    inventoryStandardVarianceLossCreditAccountId: '',
    inventoryTransferDebitAccountId: '',
    inventoryTransferCreditAccountId: ''
  };

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.operationsControlRoomChanged))
      .subscribe(() => this.load(false));

    void this.setupLiveSubscription();
    this.load();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.getOperationsControlRoom().subscribe({
      next: summary => {
        this.summary.set(summary);
        this.loading.set(false);
      },
      error: () => {
        this.summary.set(null);
        this.loading.set(false);
      }
    });
    this.loadSlaEscalations();
    this.loadFinancialPostings();
    this.loadPostingProfile();
    this.loadHospitalityKpi();
  }

  loadHospitalityKpi(): void {
    this.service.listHospitalityProperties().subscribe({
      next: (properties: HospitalityProperty[]) => {
        const property = properties[0];
        if (!property) {
          this.hospitalityKpi.set(null);
          return;
        }
        this.service.getHospitalityKpi(property.id).subscribe({
          next: kpi => this.hospitalityKpi.set(kpi),
          error: () => this.hospitalityKpi.set(null)
        });
      },
      error: () => this.hospitalityKpi.set(null)
    });
  }

  acknowledgeSla(id: string): void {
    this.service.acknowledgeServiceSlaEscalation(id).subscribe({
      next: () => this.loadSlaEscalations(),
      error: () => {}
    });
  }

  bootstrapPhase3Posting(): void {
    this.bootstrappingPosting.set(true);
    this.postingMessage.set('');
    const currency = (this.postingForm.defaultCurrencyCode ?? '').trim() || 'IRR';
    this.service.bootstrapPhase3PostingProfile(currency).subscribe({
      next: result => {
        this.bootstrappingPosting.set(false);
        this.postingMessage.set(
          this.t('catalog.controlRoom.bootstrapPhase3Success')
            .replace('{book}', result.financialBookCode)
            .replace('{count}', String(result.assetBooksUpdated))
        );
        this.loadPostingProfile();
        this.loadFinancialPostings();
      },
      error: () => {
        this.bootstrappingPosting.set(false);
        this.postingMessage.set(this.t('catalog.controlRoom.bootstrapPhase3Failed'));
      }
    });
  }

  bootstrapPhase4Posting(): void {
    this.bootstrappingPosting.set(true);
    this.postingMessage.set('');
    const currency = (this.postingForm.defaultCurrencyCode ?? '').trim() || 'IRR';
    this.service.bootstrapPhase4PostingProfile(currency).subscribe({
      next: result => {
        this.bootstrappingPosting.set(false);
        this.postingMessage.set(
          this.t('catalog.controlRoom.bootstrapPhase4Success')
            .replace('{book}', result.financialBookCode)
        );
        this.loadPostingProfile();
        this.loadFinancialPostings();
      },
      error: () => {
        this.bootstrappingPosting.set(false);
        this.postingMessage.set(this.t('catalog.controlRoom.bootstrapPhase4Failed'));
      }
    });
  }

  bootstrapPhase5Posting(): void {
    this.bootstrappingPosting.set(true);
    this.postingMessage.set('');
    const currency = (this.postingForm.defaultCurrencyCode ?? '').trim() || 'IRR';
    this.service.bootstrapPhase5PostingProfile(currency).subscribe({
      next: result => {
        this.bootstrappingPosting.set(false);
        this.postingMessage.set(
          this.t('catalog.controlRoom.bootstrapPhase5Success')
            .replace('{book}', result.financialBookCode)
        );
        this.loadPostingProfile();
        this.loadFinancialPostings();
      },
      error: () => {
        this.bootstrappingPosting.set(false);
        this.postingMessage.set(this.t('catalog.controlRoom.bootstrapPhase5Failed'));
      }
    });
  }

  savePostingProfile(): void {
    this.savingPosting.set(true);
    this.postingMessage.set('');
    const payload: UpsertCatalogOperationsPostingProfilePayload = {
      isEnabled: this.postingForm.isEnabled,
      defaultCurrencyCode: (this.postingForm.defaultCurrencyCode ?? '').trim() || 'USD',
      serviceRevenueDebitAccountId: this.toNullableGuid(this.postingForm.serviceRevenueDebitAccountId),
      serviceRevenueCreditAccountId: this.toNullableGuid(this.postingForm.serviceRevenueCreditAccountId),
      servicePartsCostDebitAccountId: this.toNullableGuid(this.postingForm.servicePartsCostDebitAccountId),
      servicePartsCostCreditAccountId: this.toNullableGuid(this.postingForm.servicePartsCostCreditAccountId),
      rmaRefundDebitAccountId: this.toNullableGuid(this.postingForm.rmaRefundDebitAccountId),
      rmaRefundCreditAccountId: this.toNullableGuid(this.postingForm.rmaRefundCreditAccountId),
      warrantyExpenseDebitAccountId: this.toNullableGuid(this.postingForm.warrantyExpenseDebitAccountId),
      warrantyExpenseCreditAccountId: this.toNullableGuid(this.postingForm.warrantyExpenseCreditAccountId),
      assetCapitalizationDebitAccountId: this.toNullableGuid(this.postingForm.assetCapitalizationDebitAccountId),
      assetCapitalizationCreditAccountId: this.toNullableGuid(this.postingForm.assetCapitalizationCreditAccountId),
      assetDepreciationExpenseDebitAccountId: this.toNullableGuid(this.postingForm.assetDepreciationExpenseDebitAccountId),
      assetDepreciationCreditAccountId: this.toNullableGuid(this.postingForm.assetDepreciationCreditAccountId),
      assetDisposalGainLossDebitAccountId: this.toNullableGuid(this.postingForm.assetDisposalGainLossDebitAccountId),
      assetDisposalGainLossCreditAccountId: this.toNullableGuid(this.postingForm.assetDisposalGainLossCreditAccountId),
      rentalRevenueDebitAccountId: this.toNullableGuid(this.postingForm.rentalRevenueDebitAccountId),
      rentalRevenueCreditAccountId: this.toNullableGuid(this.postingForm.rentalRevenueCreditAccountId),
      rentalDepositDebitAccountId: this.toNullableGuid(this.postingForm.rentalDepositDebitAccountId),
      rentalDepositCreditAccountId: this.toNullableGuid(this.postingForm.rentalDepositCreditAccountId),
      rentalDamageChargeDebitAccountId: this.toNullableGuid(this.postingForm.rentalDamageChargeDebitAccountId),
      rentalDamageChargeCreditAccountId: this.toNullableGuid(this.postingForm.rentalDamageChargeCreditAccountId),
      cmmsMaintenanceExpenseDebitAccountId: this.toNullableGuid(this.postingForm.cmmsMaintenanceExpenseDebitAccountId),
      cmmsMaintenanceExpenseCreditAccountId: this.toNullableGuid(this.postingForm.cmmsMaintenanceExpenseCreditAccountId),
      cmmsSparePartConsumptionDebitAccountId: this.toNullableGuid(this.postingForm.cmmsSparePartConsumptionDebitAccountId),
      cmmsSparePartConsumptionCreditAccountId: this.toNullableGuid(this.postingForm.cmmsSparePartConsumptionCreditAccountId),
      hospitalityPrepaymentDebitAccountId: this.toNullableGuid(this.postingForm.hospitalityPrepaymentDebitAccountId),
      hospitalityPrepaymentCreditAccountId: this.toNullableGuid(this.postingForm.hospitalityPrepaymentCreditAccountId),
      hospitalityFolioChargeDebitAccountId: this.toNullableGuid(this.postingForm.hospitalityFolioChargeDebitAccountId),
      hospitalityFolioChargeCreditAccountId: this.toNullableGuid(this.postingForm.hospitalityFolioChargeCreditAccountId),
      hospitalityRefundDebitAccountId: this.toNullableGuid(this.postingForm.hospitalityRefundDebitAccountId),
      hospitalityRefundCreditAccountId: this.toNullableGuid(this.postingForm.hospitalityRefundCreditAccountId),
      hospitalityMinibarCogsDebitAccountId: this.toNullableGuid(this.postingForm.hospitalityMinibarCogsDebitAccountId),
      hospitalityMinibarCogsCreditAccountId: this.toNullableGuid(this.postingForm.hospitalityMinibarCogsCreditAccountId),
      hospitalityChannelCommissionDebitAccountId: this.toNullableGuid(this.postingForm.hospitalityChannelCommissionDebitAccountId),
      hospitalityChannelCommissionCreditAccountId: this.toNullableGuid(this.postingForm.hospitalityChannelCommissionCreditAccountId),
      inventoryIssueDebitAccountId: this.toNullableGuid(this.postingForm.inventoryIssueDebitAccountId),
      inventoryIssueCreditAccountId: this.toNullableGuid(this.postingForm.inventoryIssueCreditAccountId),
      inventoryCogsDebitAccountId: this.toNullableGuid(this.postingForm.inventoryCogsDebitAccountId),
      inventoryCogsCreditAccountId: this.toNullableGuid(this.postingForm.inventoryCogsCreditAccountId),
      inventoryReceiptDebitAccountId: this.toNullableGuid(this.postingForm.inventoryReceiptDebitAccountId),
      inventoryReceiptCreditAccountId: this.toNullableGuid(this.postingForm.inventoryReceiptCreditAccountId),
      inventoryAdjustmentGainDebitAccountId: this.toNullableGuid(this.postingForm.inventoryAdjustmentGainDebitAccountId),
      inventoryAdjustmentGainCreditAccountId: this.toNullableGuid(this.postingForm.inventoryAdjustmentGainCreditAccountId),
      inventoryAdjustmentLossDebitAccountId: this.toNullableGuid(this.postingForm.inventoryAdjustmentLossDebitAccountId),
      inventoryAdjustmentLossCreditAccountId: this.toNullableGuid(this.postingForm.inventoryAdjustmentLossCreditAccountId),
      inventoryLandedCostDebitAccountId: this.toNullableGuid(this.postingForm.inventoryLandedCostDebitAccountId),
      inventoryLandedCostCreditAccountId: this.toNullableGuid(this.postingForm.inventoryLandedCostCreditAccountId),
      inventoryRevaluationGainDebitAccountId: this.toNullableGuid(this.postingForm.inventoryRevaluationGainDebitAccountId),
      inventoryRevaluationGainCreditAccountId: this.toNullableGuid(this.postingForm.inventoryRevaluationGainCreditAccountId),
      inventoryRevaluationLossDebitAccountId: this.toNullableGuid(this.postingForm.inventoryRevaluationLossDebitAccountId),
      inventoryRevaluationLossCreditAccountId: this.toNullableGuid(this.postingForm.inventoryRevaluationLossCreditAccountId),
      inventoryStandardVarianceGainDebitAccountId: this.toNullableGuid(this.postingForm.inventoryStandardVarianceGainDebitAccountId),
      inventoryStandardVarianceGainCreditAccountId: this.toNullableGuid(this.postingForm.inventoryStandardVarianceGainCreditAccountId),
      inventoryStandardVarianceLossDebitAccountId: this.toNullableGuid(this.postingForm.inventoryStandardVarianceLossDebitAccountId),
      inventoryStandardVarianceLossCreditAccountId: this.toNullableGuid(this.postingForm.inventoryStandardVarianceLossCreditAccountId),
      inventoryTransferDebitAccountId: this.toNullableGuid(this.postingForm.inventoryTransferDebitAccountId),
      inventoryTransferCreditAccountId: this.toNullableGuid(this.postingForm.inventoryTransferCreditAccountId)
    };
    this.service.upsertOperationsPostingProfile(payload).subscribe({
      next: () => {
        this.savingPosting.set(false);
        this.postingMessage.set(this.t('catalog.controlRoom.postingSaved'));
        this.loadFinancialPostings();
      },
      error: () => {
        this.savingPosting.set(false);
        this.postingMessage.set(this.t('catalog.controlRoom.postingSaveFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;

    this.subscribedTopic = operationsControlRoomTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }

  getPostingAccountValue(key: PostingAccountFieldKey): string | null {
    const value = this.postingForm[key];
    return value?.trim() ? value : null;
  }

  setPostingAccountValue(key: PostingAccountFieldKey, value: string | null): void {
    this.postingForm[key] = value ?? '';
  }

  private loadSlaEscalations(): void {
    this.service.listServiceSlaEscalations('Open').subscribe({
      next: rows => this.slaEscalations.set(rows),
      error: () => this.slaEscalations.set([])
    });
  }

  private loadFinancialPostings(): void {
    this.service.listFinancialPostingRecords().subscribe({
      next: rows => this.financialPostings.set(rows),
      error: () => this.financialPostings.set([])
    });
  }

  private loadPostingProfile(): void {
    this.service.getOperationsPostingProfile().subscribe({
      next: profile => this.applyPostingProfile(profile),
      error: () => {}
    });
  }

  private applyPostingProfile(profile: CatalogOperationsPostingProfile): void {
    this.postingForm.isEnabled = profile.isEnabled;
    this.postingForm.defaultCurrencyCode = profile.defaultCurrencyCode || 'USD';
    this.postingForm.serviceRevenueDebitAccountId = profile.serviceRevenueDebitAccountId ?? '';
    this.postingForm.serviceRevenueCreditAccountId = profile.serviceRevenueCreditAccountId ?? '';
    this.postingForm.servicePartsCostDebitAccountId = profile.servicePartsCostDebitAccountId ?? '';
    this.postingForm.servicePartsCostCreditAccountId = profile.servicePartsCostCreditAccountId ?? '';
    this.postingForm.rmaRefundDebitAccountId = profile.rmaRefundDebitAccountId ?? '';
    this.postingForm.rmaRefundCreditAccountId = profile.rmaRefundCreditAccountId ?? '';
    this.postingForm.warrantyExpenseDebitAccountId = profile.warrantyExpenseDebitAccountId ?? '';
    this.postingForm.warrantyExpenseCreditAccountId = profile.warrantyExpenseCreditAccountId ?? '';
    this.postingForm.assetCapitalizationDebitAccountId = profile.assetCapitalizationDebitAccountId ?? '';
    this.postingForm.assetCapitalizationCreditAccountId = profile.assetCapitalizationCreditAccountId ?? '';
    this.postingForm.assetDepreciationExpenseDebitAccountId = profile.assetDepreciationExpenseDebitAccountId ?? '';
    this.postingForm.assetDepreciationCreditAccountId = profile.assetDepreciationCreditAccountId ?? '';
    this.postingForm.assetDisposalGainLossDebitAccountId = profile.assetDisposalGainLossDebitAccountId ?? '';
    this.postingForm.assetDisposalGainLossCreditAccountId = profile.assetDisposalGainLossCreditAccountId ?? '';
    this.postingForm.rentalRevenueDebitAccountId = profile.rentalRevenueDebitAccountId ?? '';
    this.postingForm.rentalRevenueCreditAccountId = profile.rentalRevenueCreditAccountId ?? '';
    this.postingForm.rentalDepositDebitAccountId = profile.rentalDepositDebitAccountId ?? '';
    this.postingForm.rentalDepositCreditAccountId = profile.rentalDepositCreditAccountId ?? '';
    this.postingForm.rentalDamageChargeDebitAccountId = profile.rentalDamageChargeDebitAccountId ?? '';
    this.postingForm.rentalDamageChargeCreditAccountId = profile.rentalDamageChargeCreditAccountId ?? '';
    this.postingForm.cmmsMaintenanceExpenseDebitAccountId = profile.cmmsMaintenanceExpenseDebitAccountId ?? '';
    this.postingForm.cmmsMaintenanceExpenseCreditAccountId = profile.cmmsMaintenanceExpenseCreditAccountId ?? '';
    this.postingForm.cmmsSparePartConsumptionDebitAccountId = profile.cmmsSparePartConsumptionDebitAccountId ?? '';
    this.postingForm.cmmsSparePartConsumptionCreditAccountId = profile.cmmsSparePartConsumptionCreditAccountId ?? '';
    this.postingForm.hospitalityPrepaymentDebitAccountId = profile.hospitalityPrepaymentDebitAccountId ?? '';
    this.postingForm.hospitalityPrepaymentCreditAccountId = profile.hospitalityPrepaymentCreditAccountId ?? '';
    this.postingForm.hospitalityFolioChargeDebitAccountId = profile.hospitalityFolioChargeDebitAccountId ?? '';
    this.postingForm.hospitalityFolioChargeCreditAccountId = profile.hospitalityFolioChargeCreditAccountId ?? '';
    this.postingForm.hospitalityRefundDebitAccountId = profile.hospitalityRefundDebitAccountId ?? '';
    this.postingForm.hospitalityRefundCreditAccountId = profile.hospitalityRefundCreditAccountId ?? '';
    this.postingForm.hospitalityMinibarCogsDebitAccountId = profile.hospitalityMinibarCogsDebitAccountId ?? '';
    this.postingForm.hospitalityMinibarCogsCreditAccountId = profile.hospitalityMinibarCogsCreditAccountId ?? '';
    this.postingForm.hospitalityChannelCommissionDebitAccountId = profile.hospitalityChannelCommissionDebitAccountId ?? '';
    this.postingForm.hospitalityChannelCommissionCreditAccountId = profile.hospitalityChannelCommissionCreditAccountId ?? '';
    this.postingForm.inventoryIssueDebitAccountId = profile.inventoryIssueDebitAccountId ?? '';
    this.postingForm.inventoryIssueCreditAccountId = profile.inventoryIssueCreditAccountId ?? '';
    this.postingForm.inventoryCogsDebitAccountId = profile.inventoryCogsDebitAccountId ?? '';
    this.postingForm.inventoryCogsCreditAccountId = profile.inventoryCogsCreditAccountId ?? '';
    this.postingForm.inventoryReceiptDebitAccountId = profile.inventoryReceiptDebitAccountId ?? '';
    this.postingForm.inventoryReceiptCreditAccountId = profile.inventoryReceiptCreditAccountId ?? '';
    this.postingForm.inventoryAdjustmentGainDebitAccountId = profile.inventoryAdjustmentGainDebitAccountId ?? '';
    this.postingForm.inventoryAdjustmentGainCreditAccountId = profile.inventoryAdjustmentGainCreditAccountId ?? '';
    this.postingForm.inventoryAdjustmentLossDebitAccountId = profile.inventoryAdjustmentLossDebitAccountId ?? '';
    this.postingForm.inventoryAdjustmentLossCreditAccountId = profile.inventoryAdjustmentLossCreditAccountId ?? '';
    this.postingForm.inventoryLandedCostDebitAccountId = profile.inventoryLandedCostDebitAccountId ?? '';
    this.postingForm.inventoryLandedCostCreditAccountId = profile.inventoryLandedCostCreditAccountId ?? '';
    this.postingForm.inventoryRevaluationGainDebitAccountId = profile.inventoryRevaluationGainDebitAccountId ?? '';
    this.postingForm.inventoryRevaluationGainCreditAccountId = profile.inventoryRevaluationGainCreditAccountId ?? '';
    this.postingForm.inventoryRevaluationLossDebitAccountId = profile.inventoryRevaluationLossDebitAccountId ?? '';
    this.postingForm.inventoryRevaluationLossCreditAccountId = profile.inventoryRevaluationLossCreditAccountId ?? '';
    this.postingForm.inventoryStandardVarianceGainDebitAccountId = profile.inventoryStandardVarianceGainDebitAccountId ?? '';
    this.postingForm.inventoryStandardVarianceGainCreditAccountId = profile.inventoryStandardVarianceGainCreditAccountId ?? '';
    this.postingForm.inventoryStandardVarianceLossDebitAccountId = profile.inventoryStandardVarianceLossDebitAccountId ?? '';
    this.postingForm.inventoryStandardVarianceLossCreditAccountId = profile.inventoryStandardVarianceLossCreditAccountId ?? '';
    this.postingForm.inventoryTransferDebitAccountId = profile.inventoryTransferDebitAccountId ?? '';
    this.postingForm.inventoryTransferCreditAccountId = profile.inventoryTransferCreditAccountId ?? '';
  }

  private toNullableGuid(value: string | null | undefined): string | null {
    const trimmed = (value ?? '').trim();
    return trimmed.length === 0 ? null : trimmed;
  }
}

type PostingAccountFieldKey =
  | 'serviceRevenueDebitAccountId'
  | 'serviceRevenueCreditAccountId'
  | 'servicePartsCostDebitAccountId'
  | 'servicePartsCostCreditAccountId'
  | 'rmaRefundDebitAccountId'
  | 'rmaRefundCreditAccountId'
  | 'warrantyExpenseDebitAccountId'
  | 'warrantyExpenseCreditAccountId'
  | 'assetCapitalizationDebitAccountId'
  | 'assetCapitalizationCreditAccountId'
  | 'assetDepreciationExpenseDebitAccountId'
  | 'assetDepreciationCreditAccountId'
  | 'assetDisposalGainLossDebitAccountId'
  | 'assetDisposalGainLossCreditAccountId'
  | 'rentalRevenueDebitAccountId'
  | 'rentalRevenueCreditAccountId'
  | 'rentalDepositDebitAccountId'
  | 'rentalDepositCreditAccountId'
  | 'rentalDamageChargeDebitAccountId'
  | 'rentalDamageChargeCreditAccountId'
  | 'cmmsMaintenanceExpenseDebitAccountId'
  | 'cmmsMaintenanceExpenseCreditAccountId'
  | 'cmmsSparePartConsumptionDebitAccountId'
  | 'cmmsSparePartConsumptionCreditAccountId'
  | 'hospitalityPrepaymentDebitAccountId'
  | 'hospitalityPrepaymentCreditAccountId'
  | 'hospitalityFolioChargeDebitAccountId'
  | 'hospitalityFolioChargeCreditAccountId'
  | 'hospitalityRefundDebitAccountId'
  | 'hospitalityRefundCreditAccountId'
  | 'hospitalityMinibarCogsDebitAccountId'
  | 'hospitalityMinibarCogsCreditAccountId'
  | 'hospitalityChannelCommissionDebitAccountId'
  | 'hospitalityChannelCommissionCreditAccountId'
  | 'inventoryIssueDebitAccountId'
  | 'inventoryIssueCreditAccountId'
  | 'inventoryCogsDebitAccountId'
  | 'inventoryCogsCreditAccountId'
  | 'inventoryReceiptDebitAccountId'
  | 'inventoryReceiptCreditAccountId'
  | 'inventoryAdjustmentGainDebitAccountId'
  | 'inventoryAdjustmentGainCreditAccountId'
  | 'inventoryAdjustmentLossDebitAccountId'
  | 'inventoryAdjustmentLossCreditAccountId'
  | 'inventoryLandedCostDebitAccountId'
  | 'inventoryLandedCostCreditAccountId'
  | 'inventoryRevaluationGainDebitAccountId'
  | 'inventoryRevaluationGainCreditAccountId'
  | 'inventoryRevaluationLossDebitAccountId'
  | 'inventoryRevaluationLossCreditAccountId'
  | 'inventoryStandardVarianceGainDebitAccountId'
  | 'inventoryStandardVarianceGainCreditAccountId'
  | 'inventoryStandardVarianceLossDebitAccountId'
  | 'inventoryStandardVarianceLossCreditAccountId'
  | 'inventoryTransferDebitAccountId'
  | 'inventoryTransferCreditAccountId';
